import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { DeviceEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createDeviceLink, createGroupLink, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type DeviceCreatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Created" }>;
const renderDeviceCreated = (props: RendererProps<DeviceCreatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);
    const actorTitle = getActorTitle(event);

    const deviceLabel = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const actorLabel = actorTitle ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceCreatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceCreatedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceUpdatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Updated" }>;
const renderDeviceUpdated = (props: RendererProps<DeviceUpdatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const actorLabel = getActorTitle(event) ?? i18next.t("common:status.unknown");

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.deviceUpdatedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.deviceUpdatedDescriptionWithoutData"
                    values={{
                        actorTitle: actorLabel,
                        entityId: event.entityId,
                    }}
                />
            ),
        };
    }

    const deviceName = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const oldVersion = event.data.emteriaVersion.old;
    const newVersion = event.data.emteriaVersion.new;

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceUpdatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceUpdatedDescription"
                values={{
                    deviceName,
                    groupName,
                    oldVersion,
                    newVersion,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceActivatedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Activated" }>;
const renderDeviceActivated = (props: RendererProps<DeviceActivatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceName = device?.name ?? event.entityId ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceActivatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceActivatedDescription"
                values={{
                    deviceName,
                    groupName,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceLocationChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.LocationChanged" }>;
const renderDeviceLocationChanged = (props: RendererProps<DeviceLocationChangedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceLocationChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceLocationChangedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceBatteryAlertDetectedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.BatteryAlertDetected" }>;
const renderDeviceBatteryAlertDetected = (props: RendererProps<DeviceBatteryAlertDetectedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const percentage = event.data?.battery?.new ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceBatteryAlertDetectedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceBatteryAlertDetectedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                    percentage,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceMemoryAlertDetectedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.MemoryAlertDetected" }>;
const renderDeviceMemoryAlertDetected = (props: RendererProps<DeviceMemoryAlertDetectedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);

    const deviceLabel = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceMemoryAlertDetectedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceMemoryAlertDetectedDescription"
                values={{
                    deviceName: deviceLabel,
                    groupName: groupLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceRenamedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Renamed" }>;
const renderDeviceRenamed = (props: RendererProps<DeviceRenamedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;
    const color = getEventColors(theme);
    const actorTitle = getActorTitle(event);

    const deviceLabel = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const actorLabel = actorTitle ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceRenamedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceRenamedDescription"
                values={{
                    newName: deviceLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceDeletedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.Deleted" }>;
const renderDeviceDeleted = (props: RendererProps<DeviceDeletedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const color = getEventColors(theme);
    const actorTitle = getActorTitle(event);

    const deviceLabel = event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupLabel = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const actorLabel = actorTitle ?? i18next.t("common:status.unknown");

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceDeletedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceDeletedDescription"
                values={{
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                    deviceSerial: deviceLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type DeviceBatteryLevelChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.BatteryLevelChanged" }>;
const renderDeviceBatteryLevelChanged = (props: RendererProps<DeviceBatteryLevelChangedEvent>): EventContentResult | null => {
    const { event, theme } = props;

    const color = getEventColors(theme);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceBatteryLevelChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceBatteryLevelChangedDescription"
                values={{
                    entityId: event.entityId,
                }}
            />
        ),
    };
};

type DeviceMemoryLevelChangedEvent = Extract<DeviceEvents, { eventTypeName: "DeviceEvent.MemoryLevelChanged" }>;
const renderDeviceMemoryLevelChanged = (props: RendererProps<DeviceMemoryLevelChangedEvent>): EventContentResult | null => {
    const { event, theme } = props;

    const actorLabel = getActorTitle(event) ?? i18next.t("common:status.unknown");
    const color = getEventColors(theme);

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.deviceMemoryLevelChangedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.deviceMemoryLevelChangedDescription"
                values={{
                    actorTitle: actorLabel,
                    entityId: event.entityId,
                }}
            />
        ),
    };
};

export const renderDeviceEventContent = (event: DeviceEvents, context: EventContext, theme: Theme): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "DeviceEvent.Created":
            return renderDeviceCreated({ event, context, theme });
        case "DeviceEvent.Updated":
            return renderDeviceUpdated({ event, context, theme });
        case "DeviceEvent.Activated":
            return renderDeviceActivated({ event, context, theme });
        case "DeviceEvent.LocationChanged":
            return renderDeviceLocationChanged({ event, context, theme });
        case "DeviceEvent.BatteryAlertDetected":
            return renderDeviceBatteryAlertDetected({ event, context, theme });
        case "DeviceEvent.MemoryAlertDetected":
            return renderDeviceMemoryAlertDetected({ event, context, theme });
        case "DeviceEvent.Renamed":
            return renderDeviceRenamed({ event, context, theme });
        case "DeviceEvent.Deleted":
            return renderDeviceDeleted({ event, context, theme });
        case "DeviceEvent.BatteryLevelChanged":
            return renderDeviceBatteryLevelChanged({ event, context, theme });
        case "DeviceEvent.MemoryLevelChanged":
            return renderDeviceMemoryLevelChanged({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
